import { Conditional, Separator, ConditionOperators, IConditionObj, ThemeColor, ThemeShade, AccountPopover, ButtonWithIcon } from '@papa/components';
import styled from 'styled-components';
import { dropins } from '../dataModel/dropins';
import { Blurb, StyledSeparator } from '../styles/FormElements';
import { FirstNameDropin } from './FinalStepsEncourage';
import { QueryClient } from '@tanstack/react-query';
import { loadLogin, loadSignUp } from '../App';

export const conditionIsLoggedIn: IConditionObj<string> = {
    valueKey: 'is_logged_in',
    operator: ConditionOperators.equals,
    target: true,
};

const BannerImage = styled.img`
    object-fit: cover;
    width: 100%;
    height: 100%;
    grid-column: 1 / span 9;
    grid-row: 1 / span 5;
`;
const ApplicationHeader = styled.div`
    display: flex;
    justify-content: space-between;
    height: 3.5rem;
    align-items: center;
    padding: 1rem 2rem;
    background-color: ${props => props.theme.neutral.faint};
    @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
        padding: 2% 5%;
    }
`;
const SchoolLogo = styled.img`
    object-fit: contain;
    height: 3.5rem;
    align-self: center;
`;
const AppTitle = styled.h2`
    color: ${props => props.theme.secondary.main};
    font-family: ${props => props.theme.font.main};
    width: 18rem;

    @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
        font-size: 1em;
    }
`;
const Banner = styled.div`
    display: grid;
    grid-template-columns: repeat(9, 1fr);
    grid-template-rows: repeat(5, 1fr);
    position: relative;
    text-align: center;
`;
const WelcomeMessage = styled.div`
    border-radius: ${props => props.theme.borderRadius};
    background: ${props => props.theme.secondary.light}cc;
    color: ${props => props.theme.white};
    font-family: ${props => props.theme.font.display};
    padding: 1rem 1rem;
    grid-column: 6 / 9;
    grid-row: 2 / span 3;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    @media screen and (max-width: calc(${props => props.theme.layout.tablet} + 300px)) and (min-width: ${props => props.theme.layout.mobile}) {
        grid-column: 2 / 9;
        grid-row: 2 / span 3;
    }

    @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
        grid-column: 1 / span 9;
        grid-row: 1 / span 5;
    }
`;

const LogoContainer = styled.div`
    display: flex;
    gap: 1rem;
    @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
        max-width: 65vw;
    }
`;

const WelcomeTitle = styled.h2`
    font-family: ${props => props.theme.font.display};
    font-size: 2em;
    text-transform: uppercase;
    letter-spacing: 0.4rem;
    color: ${props => props.theme.error.light};
    margin: 0;
    padding: 0.5;
`;

const StyledAcctPopover = styled(AccountPopover.Root)`
    z-index: 100;

    &${AccountPopover.Trigger} {
        font-size: 1em;
        margin-right: 3rem;
        @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
            margin-right: 1rem;
        }
    }

    ${AccountPopover.AccountTrigger} {
        gap: 1rem;
        @media screen and (max-width: calc(${props => props.theme.layout.mobile} - 1px)) {
            gap: 0.5rem;
        }
    }

    &${AccountPopover.PopoverContainer} {
        background-color: ${props => props.theme.neutral.faint};
    }

    &${AccountPopover.AccountIcon} {
        padding: 0;
    }

    &${AccountPopover.SignInButton}, &${AccountPopover.SignUpButton} {
        box-shadow: none;
    }

    &${AccountPopover.SignInButton} {
        background-color: ${props => props.theme.primary.main};
    }

    &${AccountPopover.SignUpButton} {
        background-color: transparent;
    }

    &${AccountPopover.PopoverContainer} {
        left: 2rem;
    }

    &${AccountPopover.LogoutButton} {
        background-color: ${props => props.theme.primary.main};
        height: 2rem;
        box-shadow: none;

        &:disabled {
            background-color: ${props => props.theme.disabled.light};
        }

        &&&${ButtonWithIcon.LoadingContainer} {
            background-color: transparent;
            padding: 0 0.85rem;
        }
    }
`;

export const LogoAndTitle = () => {
    return (
        <LogoContainer>
            <SchoolLogo src="/eab-university.png" alt="School logo for Eabert University, which features Eabert holding a backpack" width="150" height="150" />
            <StyledSeparator orientation={Separator.Orientations.vertical} weight="thin" color={ThemeColor.primary} shade={ThemeShade.main} />
            <AppTitle>
                {dropins.partnerName} {dropins.applicationTitle}
            </AppTitle>
        </LogoContainer>
    );
};
export const WelcomeBanner = (props: { queryClient: QueryClient; hasWelcomeMessage: boolean }) => {
    return (
        <div role="banner">
            <ApplicationHeader>
                <LogoAndTitle />
                <StyledAcctPopover queryClient={props.queryClient} loaders={{ signUp: loadSignUp, login: loadLogin }} />
            </ApplicationHeader>
            <Banner>
                <BannerImage src="/header.webp" alt="Zoomed in on a blue sky with green tree leaves on the right and a clear glass building on the left" />
                {props.hasWelcomeMessage ? (
                    <WelcomeMessage>
                        <Conditional.ClientSnapshot
                            conditionObj={conditionIsLoggedIn}
                            renderProps={{
                                truthyChild: (
                                    <WelcomeTitle>
                                        Welcome Back
                                        <FirstNameDropin />!
                                    </WelcomeTitle>
                                ),
                                falsyChild: <WelcomeTitle>Welcome!</WelcomeTitle>,
                            }}
                        />
                        <Blurb fontSize="1.2em" color={ThemeColor.neutral} shade={ThemeShade.light}>
                            Start applying now and we'll help you set up a Password to secure your account.
                        </Blurb>
                    </WelcomeMessage>
                ) : (
                    <WelcomeMessage>
                        <WelcomeTitle>
                            Thanks
                            <FirstNameDropin />!
                        </WelcomeTitle>
                        <Blurb fontSize="1.2em" color={ThemeColor.neutral} shade={ThemeShade.light}>
                            Congratulations on completing the online portion of your application!
                            <br />
                            <br />
                            Take these last steps to finish up your application.
                        </Blurb>
                    </WelcomeMessage>
                )}
            </Banner>
        </div>
    );
};
